/**
 * The one modal wrapper.
 *
 * ── Why this exists ─────────────────────────────────────────────────────
 *
 * Every dialog in the platform console hand-rolled its own shell, and they did
 * not agree. Measured in the live DOM with each open:
 *
 *                              NewCustomerDialog   PropagatePresetDialog
 *   role + aria-modal                   ✅                   ✅
 *   accessible name                     ✅                   ❌  unnamed
 *   moves focus into the dialog         ✅                   ❌  stays on <body>
 *   closes on Escape                    ❌                   ❌
 *   background out of the a11y tree     ❌                   ❌
 *
 * The gaps landed hardest on the worst dialog to have them: the propagate
 * dialog performs a **no-undo bulk write across customers**, and a keyboard or
 * screen-reader user opening it got no announcement, no focus move, and had to
 * tab from the top of the document — past the entire price book, which was
 * still exposed behind it — to reach the controls.
 *
 * ── What it guarantees ──────────────────────────────────────────────────
 *
 * 1. **A name.** `title` becomes the dialog's accessible name via
 *    `aria-labelledby`, so it is announced on open. A dialog whose name is the
 *    question it asks ("Apply Default to existing customers?") is doing the
 *    whole job of an announcement.
 * 2. **Focus lands inside**, on the first focusable element or on whatever
 *    carries `autoFocus`, and is **restored** to the trigger on close — losing
 *    it to `<body>` means the next Tab starts from the top of the document.
 * 3. **Focus is trapped.** Tab and Shift+Tab cycle within the dialog.
 * 4. **Escape closes**, unless `dismissible={false}` — which is how a dialog
 *    mid-write refuses to vanish while its request is in flight.
 * 5. **The background is `inert`**, so it leaves the a11y tree and stops taking
 *    clicks. This is the one guarantee `aria-modal` alone does NOT give: it is
 *    advisory, and browsers still expose the DOM behind it to sequential
 *    navigation.
 *
 * ── Theming ─────────────────────────────────────────────────────────────
 *
 * Rendered outside `ThemedArea`, so the theme class is applied by hand on the
 * PANEL and never on the backdrop — a theme class sets a solid background, and
 * on the backdrop that would paint over the page instead of dimming it. This is
 * the rule CLAUDE.md records for every portal-like surface.
 */
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useTheme, getThemeClass } from './ThemeProvider'

/** Everything focusable, in DOM order. `:not([disabled])` matters — a disabled
 *  Save is common in these dialogs while a request is in flight. */
const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled])',
  'select:not([disabled])', 'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

export interface ModalProps {
  /** The dialog's accessible name. Rendered as the heading AND wired to
   *  `aria-labelledby`, so the two can never drift apart. */
  title: ReactNode
  /** One sentence under the heading. Consequential dialogs should say what
   *  cannot be undone here. */
  description?: ReactNode
  children: ReactNode
  /** Footer controls. Put the confirming action last — it is where both the eye
   *  and the tab order end up. */
  footer?: ReactNode
  onClose: () => void
  /**
   * `false` while a write is in flight: Escape and backdrop clicks stop closing
   * the dialog, so a mid-request dismissal cannot leave the operator unsure
   * whether it landed.
   */
  dismissible?: boolean
  /** Tailwind max-width for the panel. */
  maxWidth?: string
  /** The frosted-glass dialog surface (`.ws-glass-modal`, docs/DESIGN_DOC.md §10) with its entrance animation and
   *  no hairlines around the header / footer — what the share dialog and the agent's dialogs use. Default: the
   *  solid panel. */
  glass?: boolean
}

export function Modal({
  title, description, children, footer, onClose,
  dismissible = true, maxWidth = 'max-w-lg', glass = false,
}: ModalProps) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const panelRef = useRef<HTMLDivElement>(null)
  const titleId = useId()
  const descId = useId()

  // A dedicated <body> child to portal into — see the `inert` effect for why
  // being a direct child of <body> rather than a descendant of the app root is
  // load-bearing rather than tidy.
  const hostRef = useRef<HTMLDivElement | null>(null)
  const [host, setHost] = useState<HTMLDivElement | null>(null)
  useEffect(() => {
    const el = document.createElement('div')
    el.setAttribute('data-modal-host', '')
    document.body.appendChild(el)
    hostRef.current = el
    setHost(el)
    return () => { el.remove(); hostRef.current = null }
  }, [])

  // Where focus came from, so it can go back. Captured on mount, before the
  // dialog steals it.
  const returnFocusTo = useRef<HTMLElement | null>(
    typeof document !== 'undefined' ? (document.activeElement as HTMLElement | null) : null,
  )

  const close = useCallback(() => { if (dismissible) onClose() }, [dismissible, onClose])

  // Escape, and the Tab cycle.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); close(); return }
      if (e.key !== 'Tab') return
      const panel = panelRef.current
      if (!panel) return
      const items = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)]
        .filter(el => el.offsetParent !== null || el === document.activeElement)
      if (items.length === 0) { e.preventDefault(); return }
      const first = items[0]!
      const last = items[items.length - 1]!
      // Wrap in both directions. Without this, Tab off the last control lands in
      // the page behind — which `inert` now blocks, so focus would go nowhere.
      if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus() }
      else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus() }
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [close])

  // Take the rest of the app out of the a11y tree and out of pointer reach.
  // `inert` is the guarantee `aria-modal` does not actually provide.
  //
  // This is why the dialog is PORTALLED to <body>. Rendered inline it is a
  // descendant of the app root, which is itself a body child — so marking "every
  // body child except mine" inert marked the app root, and the dialog inside it
  // went inert too: no focus, no clicks, an unreachable modal. Being a direct
  // child of <body> is what makes "everything except me" a correct sweep.
  // DECLARED BEFORE the focus-restore effect, and that order is load-bearing.
  // React runs effect cleanups in declaration order, so if focus-restore came
  // first it would call `.focus()` on a trigger that was still inside an
  // `inert` subtree — which is a silent no-op. Focus went to <body> and the
  // next Tab restarted from the top of the document, which is the exact bug
  // this component exists to prevent.
  useEffect(() => {
    const host = hostRef.current
    if (!host) return
    const marked: HTMLElement[] = []
    for (const el of document.body.children) {
      const h = el as HTMLElement
      if (h === host || h.hasAttribute('inert')) continue
      h.setAttribute('inert', '')
      marked.push(h)
    }
    return () => { for (const h of marked) h.removeAttribute('inert') }
  }, [host])

  // Focus in on mount, back to the trigger on unmount. Must stay BELOW the
  // `inert` effect — see the note there.
  useEffect(() => {
    const panel = panelRef.current
    if (!panel) return
    const autoFocused = panel.querySelector<HTMLElement>('[autofocus]')
    const first = autoFocused ?? panel.querySelector<HTMLElement>(FOCUSABLE) ?? panel
    // The panel itself is the fallback target, so a dialog with no controls at
    // all still moves focus off <body> and gets announced.
    first.focus()
    const previous = returnFocusTo.current
    return () => { previous?.focus?.() }
  }, [host])

  if (!host) return null

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* No theme class here — see the header note. */}
      <div className={`fixed inset-0 bg-black/50${glass ? ' ws-anim-fade-in' : ''}`} onClick={close} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        className={glass
          // `.ws-glass-modal` pins `overflow: hidden` (unlayered — it beats a utility), so the BODY scrolls instead.
          ? `relative z-10 w-full ${maxWidth} max-h-[90vh] flex flex-col outline-none ${themeClass} ws-glass-modal ws-anim-modal-in`
          : `relative z-10 rounded-lg shadow-xl w-full ${maxWidth} max-h-[90vh] overflow-y-auto outline-none ${themeClass} themed-bg-secondary`}
      >
        <div className={glass ? 'px-5 pt-4 pb-1 shrink-0' : 'px-5 pt-4 pb-3 border-b themed-border'}>
          <h3 id={titleId} className="themed-text text-base font-medium">{title}</h3>
          {description && (
            <p id={descId} className="themed-text-muted text-xs mt-1">{description}</p>
          )}
        </div>
        {glass ? <div className="min-h-0 flex-1 overflow-y-auto">{children}</div> : children}
        {footer && (
          <div className={`px-5 py-3 flex items-center justify-end gap-2${glass ? ' shrink-0' : ' border-t themed-border'}`}>
            {footer}
          </div>
        )}
      </div>
    </div>,
    host,
  )
}
